import { useMemo } from "react";
import { buildHeaderUnits, dateToX, totalWidth } from "@/lib/gantt-utils";
import ProjectBand from "@/components/gantt/ProjectBand";
import GanttRow from "@/components/gantt/GanttRow";
import useDependencyArrows from "@/hooks/useDependencyArrows";
import useScrollToToday from "@/hooks/useScrollToToday";

const ROW_HEIGHT = 40;

function gridStep(zoomKey) {
  if (zoomKey === "day") return 1;
  if (zoomKey === "week") return 7;
  if (zoomKey === "month") return 30;
  if (zoomKey === "quarter") return 90;
  return 365;
}

function isToday(d) {
  const t = new Date();
  return (
    t.getUTCFullYear() === d.getUTCFullYear() &&
    t.getUTCMonth() === d.getUTCMonth() &&
    t.getUTCDate() === d.getUTCDate()
  );
}

export default function GanttTimeline({
  tasks,
  project,
  zoom,
  range,
  canEdit,
  criticalPath,
  criticalPathOn,
  onOpenEventPopover,
  onDeleteEvent,
  scrollRef,
  onScroll,
  registerScrollToToday,
}) {
  const pxPerDay = zoom.pxPerDay;
  const width = totalWidth(range, pxPerDay);
  const units = useMemo(() => buildHeaderUnits(range, zoom.key), [range, zoom.key]);
  const topUnits = units.filter((u) => u.level === "top");
  const bottomUnits = units.filter((u) => u.level === "bottom");
  const dateCols = project.date_columns || [];

  const dependencyArrows = useDependencyArrows({
    tasks,
    dateCols,
    range,
    pxPerDay,
    rowHeight: ROW_HEIGHT,
  });

  useScrollToToday({ scrollRef, range, pxPerDay, width, project, registerScrollToToday });

  const todayX = useMemo(() => {
    const t = new Date();
    const x = dateToX(t, range.start, pxPerDay);
    if (x == null) return null;
    if (x < 0 || x > width) return null;
    return x;
  }, [range, pxPerDay, width]);

  const rowsHeight = Math.max(ROW_HEIGHT, tasks.length * ROW_HEIGHT);

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="overflow-auto custom-scrollbar bg-slate-50 relative flex-1"
      data-testid="gantt-timeline"
    >
      {criticalPathOn && criticalPath && (
        <div
          className="sticky top-2 right-4 ml-auto mt-2 inline-flex items-center gap-2 px-2.5 py-1 bg-red-600 text-white text-[10px] font-mono-ibm uppercase tracking-widest rounded-md shadow-lg z-[25] w-fit float-right mr-3"
          data-testid="critical-path-badge"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
          Critical path · {criticalPath.criticalIds.size} task
          {criticalPath.criticalIds.size === 1 ? "" : "s"} · {criticalPath.duration}d total
        </div>
      )}

      <div style={{ width, minHeight: "100%" }} className="relative">
        {/* Sticky two-band header */}
        <div className="sticky top-0 z-20 bg-white border-b border-slate-300" style={{ width }}>
          {todayX != null && (
            <div
              className="today-label-header"
              style={{ left: todayX }}
              data-testid="today-line-label"
            >
              Today
            </div>
          )}
          <div className="flex h-9 border-b border-slate-200">
            {topUnits.map((u, i) => {
              const x = dateToX(u.start, range.start, pxPerDay);
              const w = Math.max(0, dateToX(u.end, range.start, pxPerDay) - x);
              return (
                <div
                  key={i}
                  className="flex items-center px-3 text-[11px] font-display font-bold tracking-tight text-slate-900 border-r border-slate-200 whitespace-nowrap overflow-hidden shrink-0"
                  style={{ width: w }}
                >
                  {u.label}
                </div>
              );
            })}
          </div>
          <div className="flex h-8">
            {bottomUnits.map((u, i) => {
              const x = dateToX(u.start, range.start, pxPerDay);
              const w = Math.max(0, dateToX(u.end, range.start, pxPerDay) - x);
              const highlight = u.start && isToday(u.start) && zoom.key === "day";
              return (
                <div
                  key={i}
                  className={`flex flex-col items-center justify-center border-r border-slate-200 shrink-0 ${
                    u.isWeekend ? "bg-slate-50" : ""
                  } ${highlight ? "bg-red-50" : ""}`}
                  style={{ width: w }}
                >
                  <div className={`text-[10px] font-mono-ibm ${highlight ? "text-red-600 font-semibold" : "text-slate-700"}`}>
                    {u.label}
                  </div>
                  {u.sub && (
                    <div className="text-[9px] font-mono-ibm text-slate-400 leading-none">
                      {u.sub}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Grid area */}
        <div
          className="relative gantt-grid"
          style={{
            width,
            backgroundSize: `${pxPerDay * gridStep(zoom.key)}px 100%`,
          }}
        >
          <ProjectBand project={project} range={range} pxPerDay={pxPerDay} />

          {todayX != null && (
            <div className="today-line" style={{ left: todayX }} data-testid="today-line" />
          )}

          {tasks.map((t, idx) => {
            const cp = criticalPath?.map.get(t.id);
            const isCritical = criticalPathOn && cp?.critical;
            const isMilestone = cp?.isMilestone;
            return (
              <GanttRow
                key={t.id}
                task={t}
                dateCols={dateCols}
                range={range}
                pxPerDay={pxPerDay}
                width={width}
                canEdit={canEdit}
                isCritical={isCritical}
                isMilestone={isMilestone}
                onOpenEventPopover={onOpenEventPopover}
                onDeleteEvent={onDeleteEvent}
              />
            );
          })}

          {/* Dependency arrows overlay */}
          {dependencyArrows.length > 0 && (
            <svg
              className="absolute pointer-events-none z-[15]"
              style={{ left: 0, top: 0, width, height: rowsHeight }}
              data-testid="dependency-arrows"
            >
              <defs>
                <marker id="pg-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#dc2626" />
                </marker>
                <marker id="pg-arrow-crit" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#dc2626" />
                </marker>
              </defs>
              {dependencyArrows.map((a, i) => {
                const midX = Math.max(a.sx + 8, a.tx - 14);
                const d = `M ${a.sx} ${a.sy} L ${midX} ${a.sy} L ${midX} ${a.ty} L ${a.tx - 2} ${a.ty}`;
                const edgeKey = `${a.srcIdx}->${a.dstIdx}`;
                const isCritEdge = criticalPathOn && criticalPath?.criticalEdges?.has(edgeKey);
                return (
                  <path
                    key={i}
                    d={d}
                    fill="none"
                    stroke="#dc2626"
                    strokeWidth={isCritEdge ? "2.2" : "1.5"}
                    strokeDasharray={isCritEdge ? "none" : "3 3"}
                    markerEnd={isCritEdge ? "url(#pg-arrow-crit)" : "url(#pg-arrow)"}
                    opacity={isCritEdge ? "1" : "0.7"}
                  />
                );
              })}
            </svg>
          )}

          {tasks.length === 0 && (
            <div className="h-40 flex items-center justify-center text-xs text-slate-400 font-mono-ibm uppercase tracking-widest">
              No rows to display
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
